import type { TadaDocumentNode } from 'gql.tada';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { graphql } from '@/gql/graphql';

const InvalidateListMutation = graphql(
  /* GraphQL */ `
    mutation InvalidateListCache($field: String!) {
      _local_cacheInvalidate(field: $field)
    }
  `,
);

type BidirectionalVariables = {
  where: Record<string, unknown>;
  limit: number;
};

type UseBidirectionalListOptions<
  TItem extends { id: string },
  TFutureData,
  TPastData,
  TVariables extends BidirectionalVariables,
> = {
  pause?: boolean;
  anchorValue: string;
  baseWhere: Record<string, unknown>;
  directionLimit?: number;
  fieldName: string;
  futureQuery: TadaDocumentNode<TFutureData, TVariables>;
  pastQuery: TadaDocumentNode<TPastData, TVariables>;
  selectFutureItems(data: TFutureData | undefined): readonly TItem[];
  selectPastItems(data: TPastData | undefined): readonly TItem[];
  getItemCursor(item: TItem): string | null | undefined;
  buildVariables(args: BidirectionalVariables): TVariables;
  buildAnchorPastWhere(args: {
    baseWhere: Record<string, unknown>;
    anchorValue: string;
  }): Record<string, unknown>;
  buildAnchorFutureWhere(args: {
    baseWhere: Record<string, unknown>;
    anchorValue: string;
  }): Record<string, unknown>;
  buildPreviousPageWhere(args: {
    baseWhere: Record<string, unknown>;
    cursor: string;
  }): Record<string, unknown>;
  buildNextPageWhere(args: {
    baseWhere: Record<string, unknown>;
    cursor: string;
  }): Record<string, unknown>;
};

export default function useBidirectionalList<
  TItem extends { id: string },
  TFutureData,
  TPastData,
  TVariables extends BidirectionalVariables,
>({
  pause = false,
  anchorValue,
  baseWhere,
  directionLimit = 50,
  fieldName,
  futureQuery,
  pastQuery,
  selectFutureItems,
  selectPastItems,
  getItemCursor,
  buildVariables,
  buildAnchorPastWhere,
  buildAnchorFutureWhere,
  buildPreviousPageWhere,
  buildNextPageWhere,
}: UseBidirectionalListOptions<TItem, TFutureData, TPastData, TVariables>) {
  const [pastCursor, setPastCursor] = useState(anchorValue);
  const [futureCursor, setFutureCursor] = useState(anchorValue);
  const [isFetchingPreviousPage, setIsFetchingPreviousPage] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [isFuturePaging, setIsFuturePaging] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [awaitingFreshData, setAwaitingFreshData] = useState(false);
  const [hasResetFetchStarted, setHasResetFetchStarted] = useState(false);
  const getItemCursorRef = useRef(getItemCursor);
  const [, invalidateList] = useMutation(InvalidateListMutation);

  useEffect(() => {
    getItemCursorRef.current = getItemCursor;
  }, [getItemCursor]);

  useEffect(() => {
    setPastCursor(anchorValue);
    setFutureCursor(anchorValue);
    setIsFetchingPreviousPage(false);
    setIsFetchingNextPage(false);
    setIsFuturePaging(false);
  }, [anchorValue]);

  useEffect(() => {
    if (isResetting) {
      setIsResetting(false);
    }
  }, [isResetting]);

  const shouldPause = pause || isResetting;

  const [{ data: pastData, fetching: fetchingPast }] = useQuery({
    query: pastQuery,
    pause: shouldPause,
    requestPolicy: 'network-only',
    variables: buildVariables({
      where:
        pastCursor === anchorValue
          ? buildAnchorPastWhere({
              baseWhere,
              anchorValue,
            })
          : buildPreviousPageWhere({
              baseWhere,
              cursor: pastCursor,
            }),
      limit: directionLimit,
    }),
  });

  const [{ data: futureData, fetching: fetchingFuture }] = useQuery({
    query: futureQuery,
    pause: shouldPause,
    requestPolicy: 'network-only',
    variables: buildVariables({
      where: !isFuturePaging
        ? buildAnchorFutureWhere({
            baseWhere,
            anchorValue,
          })
        : buildNextPageWhere({
            baseWhere,
            cursor: futureCursor,
          }),
      limit: directionLimit,
    }),
  });

  useEffect(() => {
    if (!fetchingPast && isFetchingPreviousPage) {
      setIsFetchingPreviousPage(false);
    }
  }, [fetchingPast, isFetchingPreviousPage]);

  useEffect(() => {
    if (!fetchingFuture && isFetchingNextPage) {
      setIsFetchingNextPage(false);
    }
  }, [fetchingFuture, isFetchingNextPage]);

  useEffect(() => {
    if (
      awaitingFreshData &&
      !hasResetFetchStarted &&
      (fetchingPast || fetchingFuture)
    ) {
      setHasResetFetchStarted(true);
    }
  }, [awaitingFreshData, hasResetFetchStarted, fetchingPast, fetchingFuture]);

  useEffect(() => {
    if (
      awaitingFreshData &&
      hasResetFetchStarted &&
      !fetchingPast &&
      !fetchingFuture &&
      !isResetting
    ) {
      setAwaitingFreshData(false);
      setHasResetFetchStarted(false);
    }
  }, [
    awaitingFreshData,
    hasResetFetchStarted,
    fetchingPast,
    fetchingFuture,
    isResetting,
  ]);

  const hideStaleData = isResetting || awaitingFreshData;
  const pastItems = hideStaleData ? [] : selectPastItems(pastData);
  const futureItems = hideStaleData ? [] : selectFutureItems(futureData);
  const mergedIds = new Set<string>();
  const listData = [...pastItems]
    .reverse()
    .concat(futureItems)
    .filter((item) => {
      if (mergedIds.has(item.id)) {
        return false;
      }

      mergedIds.add(item.id);
      return true;
    });

  const fetchPreviousPage = () => {
    if (isFetchingPreviousPage || fetchingPast) {
      return;
    }

    const oldestCursor = listData[0]
      ? getItemCursorRef.current(listData[0])
      : null;
    if (!oldestCursor) {
      return;
    }

    setIsFetchingPreviousPage(true);
    setPastCursor(oldestCursor);
  };

  const fetchNextPage = () => {
    if (isFetchingNextPage || fetchingFuture) {
      return;
    }

    const newestCursor = listData[listData.length - 1]
      ? getItemCursorRef.current(listData[listData.length - 1])
      : null;
    if (!newestCursor) {
      return;
    }

    setIsFetchingNextPage(true);
    setIsFuturePaging(true);
    setFutureCursor(newestCursor);
  };

  const resetSession = async (nextAnchorValue?: string) => {
    await invalidateList({ field: fieldName });

    const resetTo = nextAnchorValue ?? anchorValue;
    setIsResetting(true);
    setAwaitingFreshData(true);
    setHasResetFetchStarted(false);
    setPastCursor(resetTo);
    setFutureCursor(resetTo);
    setIsFetchingPreviousPage(false);
    setIsFetchingNextPage(false);
    setIsFuturePaging(false);
  };

  return {
    listData,
    isInitialFetching: hideStaleData || fetchingPast || fetchingFuture,
    isListFetching:
      fetchingPast ||
      fetchingFuture ||
      isFetchingPreviousPage ||
      isFetchingNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage,
    fetchNextPage,
    resetSession,
  };
}
